<template>
    <div
        class="date-cell"
        :class="[field.alignment, { outlined: outlined, readonly: readonly }]"
        @mouseenter="showOverlay = true"
        @mouseleave="showOverlay = false"
    >
        <!-- Düzenlenmeyen Görüntüleme -->
        <template v-if="!editing">
            {{ formattedValue }}
        </template>

        <!-- Düzenleme Modu -->
        <template v-else>
            <!-- vue-datetime bileşenine ref veriyoruz, böylece enableEdit içinde otomatik açabiliriz. -->
            <datetime
                ref="datetimePicker"
                v-model="localDate"
                :type="includeTime ? 'datetime' : 'date'"
                class="ant-calendar-picker"
                :input-class="'ant-calendar-picker'"
                @close="onDatePickerClose"
                @input="onDateTimeInput"
                :popup-container="getPopupContainer"
            ></datetime>
        </template>

        <!-- Mouse ile hücre üzerine gelindiğinde gösterilen butonlar -->
        <div v-if="!editing && showOverlay && !readonly" class="overlay-actions">
            <PermissionController :permissions="['table.update']">
                <a-button icon="edit" size="small" style="margin-right: 4px" @click="enableEdit"></a-button>
            </PermissionController>
            <PermissionController :permissions="['table.update']">
                <a-button icon="delete" type="danger" size="small" @click="deleteDate"></a-button>
            </PermissionController>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment-timezone";

/**
 * DateCell, tarih alanlarının hem ekran gösterimini hem de
 * düzenleme arayüzünü yönetir. Veri tabanında ISO format olarak
 * sakladığımız tarihi ekranda istenilen formatla gösteriyoruz.
 * Düzenleme modunda vue-datetime bileşenini otomatik açıyoruz.
 */
export default Vue.extend({
    name: "DateCell",
    props: {
        record: {
            type: Object,
            required: true,
        },
        field: {
            type: Object,
            required: true,
        },
        outlined: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editing: false,
            showOverlay: false,
            // DB'ye kaydetmek için asıl ISO format değer
            value:
                this.record && this.record.data && this.record.data[this.field._id]
                    ? this.record.data[this.field._id]
                    : "",
            // vue-datetime ile çalışırken kullanacağımız Date objesi
            localDate: null,
        };
    },
    computed: {
        timezone() {
            if (this.field?.fieldType === "record_created_at" || this.field?.fieldType === "record_updated_at") {
                return Intl.DateTimeFormat().resolvedOptions().timeZone;
            }
            return this.field?.options?.datetime?.timezone || "UTC";
        },
        format() {
            return this.field?.options?.datetime?.format || "YYYY-MM-DD";
        },
        includeTime() {
            return !!this.field?.options?.datetime?.includeTime;
        },
        timeFormat() {
            return this.field?.options?.datetime?.timeFormat || "HH:mm";
        },
        /**
         * Ekranda gösterim için moment kullanarak istenen formatı uygular
         */
        formattedValue() {
            if (!this.value) return "";
            var displayFormat = this.includeTime ? this.format + " " + this.timeFormat : this.format;
            return moment.tz(this.value, this.timezone).format(displayFormat);
        },
    },
    watch: {},
    methods: {
        onDateTimeInput(event) {
            console.log("onDateTimeInput", event);
            if (!event) return;
            var oldValue = (this.record.data && this.record.data[this.field._id]) || "";

            var isChanged = oldValue !== event;
            if (isChanged) {
                this.value = event;
                this.$emit("update-record", {
                    recordId: this.record._id,
                    fieldId: this.field._id,
                    value: event,
                });
            }
        },
        /**
         * Düzenleme moduna geçiş
         * Mevcut ISO string'i Date objesine çevirip localDate'e atıyoruz.
         * Ardından picker'ı otomatik açıyoruz.
         */
        enableEdit() {
            if (this.readonly) {
                this.$message.error("Bu alan yalnızca okunabilir durumdadır.");
                return;
            }
            this.editing = true;

            // Kaydedilmiş bir tarih varsa, Date nesnesine dönüştür
            // Yoksa bugünün tarihi ile başla
            if (this.value) {
                this.localDate = moment.tz(this.value, this.timezone).toDate();
            } else {
                this.localDate = new Date();
            }

            // vue-datetime bileşenini otomatik açmak için $nextTick kullanabiliriz
            this.$nextTick(() => {
                if (this.$refs.datetimePicker) {
                    // vue-datetime'ın open() metodu yoksa ufak bir hack gerekebilir
                    // Ancak çoğu zaman input'a focus atamak açılması için yeterli olabilir
                    // ya da bu paket versiyonunda open() metodu varsa direkt çağrılabilir.
                    // Örn: this.$refs.datetimePicker.open();
                    let inputEl = this.$refs.datetimePicker.$el.querySelector("input");
                    console.log("enableEdit inputEl", inputEl);
                    if (inputEl) {
                        inputEl.focus();
                        inputEl.click();
                    }
                }
            });
        },

        /**
         * Hücredeki tarihi temizler ve DB'ye boş olarak yollar
         */
        deleteDate() {
            this.value = "";
            this.localDate = null;
            this.$emit("update-record", {
                recordId: this.record._id,
                fieldId: this.field._id,
                value: "",
            });
        },
        /**
         * Picker kapandığında düzenleme modundan çık
         */
        onDatePickerClose() {
            this.editing = false;
        },
        
        /**
         * Popup'un konumlandırılması için doğru konteyner'ı döndürür
         * VirtualScroller tarafından tanımlanan portals konteyneri veya 
         * global tanımlanan bir konteyneri kullanır
         */
        getPopupContainer() {
            // VirtualScroller tarafından sağlanan portal konteyneri
            if (window.getAntPortalContainer) {
                return window.getAntPortalContainer();
            }
            
            // En yakın "virtual-scroller-portals" sınıfına sahip elementi bul
            const closestPortalContainer = this.$el.closest('.virtual-scroller-wrapper')?.querySelector('.virtual-scroller-portals');
            if (closestPortalContainer) {
                return closestPortalContainer;
            }
            
            // Genel document.body'ye dön (varsayılan davranış)
            return document.body;
        }
    },
});
</script>
<style>
.date-cell {
    .vdatetime-input {
        display: none !important;
    }
}
</style>

<style scoped lang="scss">
.date-cell {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 4px 8px;
    transition: all 0.3s;

    .ant-calendar-picker {
        width: 100%;
        border: none;
        min-width: 195px;
    }

    .vdatetime-input {
        display: none;
    }

    &.outlined {
        border: 1px solid #d9d9d9;
        border-radius: 4px;

        &:not(.readonly) {
            cursor: pointer;

            &:hover {
                border-color: #40a9ff;
            }

            &:active {
                border-color: #096dd9;
            }
        }

        &.readonly {
            background-color: #f5f5f5;
            border-color: #d9d9d9;
            cursor: not-allowed;
        }
    }

    &.left {
        justify-content: flex-start;
    }

    &.center {
        justify-content: center;
    }

    &.right {
        justify-content: flex-end;
    }

    .overlay-actions {
        position: absolute;
        top: 2px;
        right: 2px;
        display: flex;
        gap: 4px;
    }
}
</style>
